import { cn } from '@/lib/utils'
import { type ComponentProps, type ReactNode, useId } from 'react'
import { Checkbox as CheckboxPrimitive } from './ui/checkbox'

export interface CheckboxProps
    extends ComponentProps<typeof CheckboxPrimitive> {
    label?: ReactNode
    /**
     * Conteúdo à direita do rótulo, fora dele — para o `IconeAjuda`, que não
     * pode ficar dentro do `<label>` (clicar nele marcaria a caixa).
     */
    endAdornment?: ReactNode
    /** Classe do wrapper que envolve caixa e rótulo. Só vale com `label`. */
    containerClassName?: string
}

/**
 * Caixa de seleção com o rótulo junto.
 *
 * Use quando o valor é confirmado depois — no submit do formulário ou num
 * "salvar" explícito. Se o clique já dispara o fetch que salva, é `Switch`.
 *
 * Sem `label` devolve só a caixa, para célula de tabela e nó de árvore.
 */
export function Checkbox({
    label,
    endAdornment,
    className,
    containerClassName,
    ...props
}: CheckboxProps) {
    const idGerado = useId()
    const id = props.id ?? idGerado

    const controle = (
        <CheckboxPrimitive
            {...props}
            id={id}
            className={cn(
                'cursor-pointer hover:ring data-[state=checked]:hover:ring-2',
                'ring-inherit data-[state=checked]:ring-ring',
                className,
            )}
        />
    )

    if (label === undefined && endAdornment === undefined) return controle

    return (
        <span
            className={cn('inline-flex items-center gap-2', containerClassName)}
        >
            {label === undefined ? (
                controle
            ) : (
                // Caixa dentro do rótulo + padding com margem negativa: a área
                // de clique cobre o vão e uma folga em volta sem mexer no layout.
                <label
                    htmlFor={id}
                    className={cn(
                        '-m-2 inline-flex items-center gap-2 p-2 select-none text-foreground',
                        props.disabled
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer',
                    )}
                >
                    {controle}
                    {label}
                </label>
            )}
            {endAdornment}
        </span>
    )
}
